///
/// EarthBrowser
///
/// Created by Matt Giger on December 24, 2025
/// Copyright (c) 2025 Geodata Labs
///

/**
 * VectorTileId - Represents a tile in the standard XYZ tile scheme
 * Compatible with MapLibre, Mapbox, and OpenStreetMap tile conventions
 */
export class VectorTileId {
	/**
	 * Generate a unique key for a tile
	 */
	public static key(z: number, x: number, y: number): string {
		return `${z}/${x}/${y}`
	}

	/**
	 * Parse a tile key back into components
	 */
	public static fromKey(key: string): VectorTileId | null {
		const parts = key.split('/')
		if (parts.length !== 3) return null

		const z = parseInt(parts[0], 10)
		const x = parseInt(parts[1], 10)
		const y = parseInt(parts[2], 10)

		if (isNaN(z) || isNaN(x) || isNaN(y)) return null

		return new VectorTileId(z, x, y)
	}

	/**
	 * Create a VectorTileId from latitude, longitude, and zoom level
	 */
	public static fromLngLat(lng: number, lat: number, z: number): VectorTileId {
		const x = Math.floor((lng + 180) / 360 * (1 << z))
		const latRad = lat * Math.PI / 180
		const y = Math.floor((1 - Math.log(Math.tan(latRad) + 1 / Math.cos(latRad)) / Math.PI) / 2 * (1 << z))
		return new VectorTileId(z, x, y)
	}

	public readonly key: string
	public readonly quadkey: string

	constructor(
		public readonly z: number,
		public readonly x: number,
		public readonly y: number
	) {
		this.key = VectorTileId.key(z, x, y)
		this.quadkey = this.toQuadkey()
	}

	/**
	 * Get the parent tile (one zoom level up)
	 */
	public get parent(): VectorTileId | null {
		if (this.z === 0) return null
		return new VectorTileId(this.z - 1, this.x >> 1, this.y >> 1)
	}

	/**
	 * Get the four child tiles (one zoom level down)
	 */
	public get children(): VectorTileId[] {
		const z1 = this.z + 1
		const x2 = this.x << 1
		const y2 = this.y << 1
		return [
			new VectorTileId(z1, x2, y2),
			new VectorTileId(z1, x2 + 1, y2),
			new VectorTileId(z1, x2, y2 + 1),
			new VectorTileId(z1, x2 + 1, y2 + 1)
		]
	}

	/**
	 * Convert to quadkey format (used by Bing Maps and some other services)
	 */
	private toQuadkey(): string {
		let quadkey = ''
		for (let i = this.z; i > 0; i--) {
			let digit = 0
			const mask = 1 << (i - 1)
			if ((this.x & mask) !== 0) digit++
			if ((this.y & mask) !== 0) digit += 2
			quadkey += digit.toString()
		}
		return quadkey
	}

	/**
	 * Get the bounding box of this tile in [west, south, east, north] format
	 */
	public get bounds(): [number, number, number, number] {
		const n = 1 << this.z
		const west = this.x / n * 360 - 180
		const east = (this.x + 1) / n * 360 - 180

		const north = this.tile2lat(this.y, this.z)
		const south = this.tile2lat(this.y + 1, this.z)

		return [west, south, east, north]
	}

	/**
	 * Get the center point of this tile [lng, lat]
	 */
	public get center(): [number, number] {
		const [west, south, east, north] = this.bounds
		return [(west + east) / 2, (south + north) / 2]
	}

	/**
	 * Convert tile Y coordinate to latitude
	 */
	private tile2lat(y: number, z: number): number {
		const n = Math.PI - 2 * Math.PI * y / (1 << z)
		return 180 / Math.PI * Math.atan(0.5 * (Math.exp(n) - Math.exp(-n)))
	}

	/**
	 * Check if this tile is valid
	 */
	public get isValid(): boolean {
		if (this.z < 0 || this.z > 30) return false
		const max = 1 << this.z
		return this.x >= 0 && this.x < max && this.y >= 0 && this.y < max
	}

	/**
	 * Check if this tile contains another tile (is an ancestor)
	 */
	public contains(other: VectorTileId): boolean {
		if (this.z >= other.z) return false
		const zDiff = other.z - this.z
		return (other.x >> zDiff) === this.x && (other.y >> zDiff) === this.y
	}

	/**
	 * Check if this tile is contained by another tile (is a descendant)
	 */
	public isContainedBy(other: VectorTileId): boolean {
		return other.contains(this)
	}

	/**
	 * Get all sibling tiles (tiles with the same parent)
	 */
	public get siblings(): VectorTileId[] {
		const parent = this.parent
		if (!parent) return [this]
		return parent.children
	}

	/**
	 * Get neighboring tiles (8 surrounding tiles)
	 */
	public get neighbors(): VectorTileId[] {
		const neighbors: VectorTileId[] = []
		const max = 1 << this.z

		for (let dy = -1; dy <= 1; dy++) {
			for (let dx = -1; dx <= 1; dx++) {
				if (dx === 0 && dy === 0) continue

				let nx = this.x + dx
				const ny = this.y + dy

				// Handle wrapping around the date line
				if (nx < 0) nx += max
				if (nx >= max) nx -= max

				// Skip tiles outside valid Y range
				if (ny < 0 || ny >= max) continue

				neighbors.push(new VectorTileId(this.z, nx, ny))
			}
		}

		return neighbors
	}

	/**
	 * Convert to URL path format (e.g., for fetching from tile servers)
	 */
	public toUrlPath(extension = 'pbf'): string {
		return `${this.z}/${this.x}/${this.y}.${extension}`
	}

	/**
	 * Convert to string representation
	 */
	public toString(): string {
		return this.key
	}

	/**
	 * Clone this tile ID
	 */
	public clone(): VectorTileId {
		return new VectorTileId(this.z, this.x, this.y)
	}

	/**
	 * Check equality with another tile
	 */
	public equals(other: VectorTileId): boolean {
		return this.z === other.z && this.x === other.x && this.y === other.y
	}
}

